import { PurchaseStateAndDelivery } from "@smartrr/shared/entities/PurchaseState";
import { useMemo } from "react";

import { useCurrentBundle } from "@customer-app/account/views/OperationAquarius/AquariusInner/hooks/useCurrentBundle";

import {
  cancelObj,
  changeFreqObj,
  delayDateObj,
  editAddressObj,
  editPaymentObj,
  giftObj,
  orderNowObj,
  pauseObj,
  resumeObj,
  setDateObj,
  skipObj,
  swapObj,
  ungiftObj,
  unskipObj,
} from "../../../constants";
import { ValidViews } from "../../../OrderOptionsModalInner";
import { ButtonOption } from "../../types";
import { useBasicSubscriptionInfo } from "../useBasicSubscriptionInfo";
import { useThemeConditionals } from "../useThemeConditionals";
import { SellingPlanGroupStoreAccess } from "@customer-app/state/zustand/sellingPlanGroup";
import { ensureShopifyGid } from "@smartrr/shared/utils/ensureShopifyGid";

type ModalTypes = "orderOptions" | "manageSubscription";

type ButtonOptionRecord = {
  [K in ValidViews]?: {
    isEnabled: boolean;
    option: ButtonOption;
    modalType: ModalTypes[];
  };
};

const CoalesceToFalse = (val: boolean | undefined | null) => {
  return val ?? false;
};

export const useModalButtonOptions = (
  delivery: PurchaseStateAndDelivery,
  modalType: ModalTypes
): ButtonOption[] => {
  const purchaseState = delivery.purchaseState;
  const {
    isPaused,
    isPrepaid,
    canBeCancelled,
    changeFreqAvailable,
    setDateAvailable,
    isSendNowAvailable,
    gift,
    isDelayDateAvailable,
    sellingPlanId,
    containsOOSItems,
  } = useBasicSubscriptionInfo(delivery);

  const { themeConditionalActions } = useThemeConditionals();
  const sellingPlanShopifyId = sellingPlanId ? ensureShopifyGid("SellingPlan", sellingPlanId) : undefined;
  const sellingPlan = SellingPlanGroupStoreAccess.useSellingPlan(sellingPlanShopifyId);
  const { bundle } = useCurrentBundle(delivery);
  const hasBundle = useMemo(
    () => !!bundle || purchaseState.containsBundle,
    [purchaseState.containsBundle, bundle]
  );

  return useMemo(() => {
    const buttonOptions: ButtonOptionRecord = {
      "Order now": {
        isEnabled: CoalesceToFalse(isSendNowAvailable && themeConditionalActions?.sendNow),
        option: orderNowObj,
        modalType: ["orderOptions"],
      },
      "Edit address": {
        isEnabled: CoalesceToFalse(
          !delivery.isSkipped &&
            (modalType === "orderOptions"
              ? themeConditionalActions?.editAddress
              : themeConditionalActions?.editSubscriptionAddress)
        ),
        option: editAddressObj,
        modalType: ["orderOptions", "manageSubscription"],
      },
      Skip: {
        isEnabled: CoalesceToFalse(!gift && themeConditionalActions?.skip),
        option: delivery.isSkipped ? unskipObj : skipObj,
        modalType: ["orderOptions"],
      },
      Gift: {
        isEnabled: CoalesceToFalse(!delivery.isSkipped && themeConditionalActions?.gift),
        option: gift ? ungiftObj : giftObj,
        modalType: ["orderOptions"],
      },
      "Swap item": {
        isEnabled: CoalesceToFalse(
          !delivery.isSkipped &&
            (!isPrepaid || !!sellingPlan?.enableSwappingByCustomers) &&
            !hasBundle &&
            (modalType === "orderOptions"
              ? themeConditionalActions?.swap
              : themeConditionalActions?.subscriptionSwap)
        ),
        option: swapObj,
        modalType: ["manageSubscription", "orderOptions"],
      },
      "Delay date": {
        isEnabled: CoalesceToFalse(
          !delivery.isSkipped && !isPrepaid && isDelayDateAvailable && themeConditionalActions?.delay
        ),
        option: delayDateObj,
        modalType: ["orderOptions"],
      },

      "Edit frequency": {
        isEnabled: CoalesceToFalse(!isPaused && changeFreqAvailable && themeConditionalActions?.editFrequency),
        option: changeFreqObj,
        modalType: ["manageSubscription"],
      },
      "Set next date": {
        isEnabled: CoalesceToFalse(!isPaused && setDateAvailable && themeConditionalActions?.setNextOrderDate),
        option: setDateObj,
        modalType: ["manageSubscription"],
      },
      "Edit payment": {
        isEnabled: CoalesceToFalse(!isPaused && themeConditionalActions?.editPayment),
        option: editPaymentObj,
        modalType: ["manageSubscription"],
      },
      Pause: {
        isEnabled: CoalesceToFalse(themeConditionalActions?.pause && !isPaused),
        option: pauseObj,
        modalType: ["manageSubscription"],
      },
      Resume: {
        isEnabled: CoalesceToFalse(isPaused && themeConditionalActions?.pause && !containsOOSItems),
        option: resumeObj,
        modalType: ["manageSubscription"],
      },
      Cancel: {
        isEnabled: CoalesceToFalse(canBeCancelled),
        option: cancelObj,
        modalType: ["manageSubscription"],
      },
    };

    const filteredOptions = Object.values(buttonOptions).filter(option => {
      return option && option.option && option.isEnabled && option.modalType.includes(modalType);
    });

    return filteredOptions.map((option): ButtonOption => {
      return option?.option!;
    });
  }, [delivery]);
};
